import { useState } from 'react'
import {
  Home, FileText, ShieldAlert, Clock,
  Upload, Cpu, CheckCircle2, Download,
  Zap, Sparkles, ShieldCheck, TrendingUp, ArrowRight,
} from 'lucide-react'
import api from '../api/client'
import UploadCard from './components/UploadCard'
import RaidUploadCard from './components/RaidUploadCard'
import TvrUploadCard from './components/TvrUploadCard'
import ProgressCard from './components/ProgressCard'
import ResultCard from './components/ResultCard'
import JobRunHistoryPage from './JobRunHistoryPage'
import './compliance.css'

const TAB = Object.freeze({
  HOME:    'home',
  SMP:     'smp',
  RAID:    'raid',
  TVR:     'tvr',
  HISTORY: 'history',
})

const TABS = [
  { key: TAB.HOME,    label: 'Home',           Icon: Home },
  { key: TAB.SMP,     label: 'SMP Generation', Icon: FileText },
  { key: TAB.RAID,    label: 'RAID Tracker',   Icon: ShieldAlert },
  { key: TAB.TVR,     label: 'Track Value',    Icon: TrendingUp },
  { key: TAB.HISTORY, label: 'Run History',    Icon: Clock },
]

const INITIAL_FEATURE_STATE = { phase: 'upload', jobId: null, jobData: null, error: null }

const FEATURE_META = {
  [TAB.SMP]: {
    title: 'SMP Generation',
    sub: 'Upload a Statement of Work and the pipeline will extract all relevant fields and populate the SMP template automatically.',
  },
  [TAB.RAID]: {
    title: 'RAID Tracker',
    sub: 'Upload a Statement of Work and the pipeline will identify risks, assumptions, issues and dependencies and populate the RAID tracker automatically.',
  },
  [TAB.TVR]: {
    title: 'Track Value Repository',
    sub: 'Upload a Statement of Work and the pipeline will identify value-add opportunities beyond the contracted scope and populate the TVR tracker automatically.',
  },
}

const HOW_IT_WORKS = [
  {
    Icon: Upload,
    heading: 'Upload your SOW',
    detail: 'Drop your Statement of Work (.pdf or .docx) into the upload area of the feature you want to run.',
  },
  {
    Icon: Cpu,
    heading: 'AI Analysis',
    detail: 'The pipeline parses, chunks, and embeds your document, then runs Azure OpenAI inference to extract all relevant data.',
  },
  {
    Icon: CheckCircle2,
    heading: 'Monitor Progress',
    detail: 'Watch the pipeline run step-by-step with live logs and a progress indicator so you always know what\'s happening.',
  },
  {
    Icon: Download,
    heading: 'Download Artifact',
    detail: 'Receive a fully populated SMP or RAID tracker — ready to share, no editing needed.',
  },
]

const KEY_FEATURES = [
  {
    Icon: Zap,
    title: 'Fully automated',
    desc: 'End-to-end pipelines with up to 8 steps run automatically — no manual copy-paste from your SOW.',
  },
  {
    Icon: Sparkles,
    title: 'Azure OpenAI powered',
    desc: 'Leverages Azure OpenAI with structured prompts and retrieval-augmented generation for high-accuracy extraction.',
  },
  {
    Icon: ShieldCheck,
    title: 'Multiple output formats',
    desc: 'SMP Generation outputs .docx; RAID Tracker outputs .xlsx — both ready for immediate distribution.',
  },
]

function HomeTab({ onNavigate }) {
  return (
    <>
      {/* Hero card */}
      <div className="relative overflow-hidden bg-white border border-gray-200 rounded-xl p-8">
        {/* <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-600 via-indigo-500 to-teal-500" /> */}
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-slate-600 bg-blue-50 px-2.5 py-1 rounded mb-3.5">
            <Cpu size={11} />
            Intelligent Document Processing
          </div>
          <h1 className="text-[1.75rem] font-bold text-slate-900 mb-2.5 leading-tight">
            Transform SOWs into <em className="not-italic text-blue-600">structured artifacts</em> in minutes
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed m-0 max-w-2xl">
            Upload a Statement of Work and IDP automatically generates your SMP, RAID Tracker, or
            Track Value Repository — extracting, analyzing, and populating every field using Azure OpenAI.
          </p>
        </div>
      </div>

      <div>
        <h2 className="idp-section-label">Choose a module</h2>
        <div className="grid grid-cols-3 gap-3.5">

          <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-[0_4px_18px_rgba(37,99,235,0.1)]"
            onClick={() => onNavigate(TAB.SMP)}>
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
              <FileText size={20} />
            </div>
            <p className="text-sm font-semibold text-slate-900 m-0">SMP Generation</p>
            <p className="text-xs text-slate-500 leading-relaxed m-0 flex-1">Auto-generates a fully populated Service Management Plan from your SOW with field extraction and confidence scoring.</p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-50 text-blue-700 hover:bg-blue-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
               onClick={e => { e.stopPropagation(); onNavigate(TAB.SMP) }}>
              <ArrowRight size={12} /> Generate SMP
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-500 hover:shadow-[0_4px_18px_rgba(85,64,216,0.1)]"
            onClick={() => onNavigate(TAB.RAID)}>
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
              <ShieldAlert size={20} />
            </div>
            <p className="text-sm font-semibold text-slate-900 m-0">RAID Tracker</p>
            <p className="text-xs text-slate-500 leading-relaxed m-0 flex-1">Identifies risks, assumptions, issues and dependencies from your SOW and populates a structured RAID tracker Excel file.</p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
              onClick={e => { e.stopPropagation(); onNavigate(TAB.RAID) }}>
              <ArrowRight size={12} /> Generate RAID
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-teal-500 hover:shadow-[0_4px_18px_rgba(13,150,136,0.1)]"
            onClick={() => onNavigate(TAB.TVR)}>
            <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600 flex-shrink-0">
              <TrendingUp size={20} />
            </div>
            <p className="text-sm font-semibold text-slate-900 m-0 flex items-center gap-2">
              Track Value
              <span className="text-[9px] font-bold tracking-wide bg-teal-50 text-teal-600 px-1.5 py-0.5 rounded">NEW</span>
            </p>
            <p className="text-xs text-slate-500 leading-relaxed m-0 flex-1">Surfaces proactive value opportunities beyond the contracted SOW scope — helping teams demonstrate extra delivery impact.</p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
              onClick={e => { e.stopPropagation(); onNavigate(TAB.TVR) }}>
              <ArrowRight size={12} /> Generate TVR
            </button>
          </div>

        </div>
      </div>

      {/* How it works */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="idp-section-label">How it works</h2>
        <div className="grid grid-cols-4 gap-0 relative">
          <div className="absolute top-[17px] left-[calc(12.5%+17px)] right-[calc(12.5%+17px)] h-px bg-gray-200" />
          {HOW_IT_WORKS.map((step) => (
            <div key={step.heading} className="flex flex-col items-center text-center gap-2.5 px-2.5">
              <div className="w-9 h-9 rounded-full bg-blue-50 border-2 border-gray-200 flex items-center justify-center text-blue-600 relative z-10 flex-shrink-0">
                <step.Icon size={17} />
              </div>
              <p className="text-xs font-semibold text-slate-800 m-0 leading-tight">{step.heading}</p>
              <p className="text-[11.5px] text-slate-500 leading-relaxed m-0">{step.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="idp-section-label">Key features</h2>
        <div className="grid grid-cols-3 gap-3.5">
          {KEY_FEATURES.map((feat) => (
            <div key={feat.title} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 mb-3">
                <feat.Icon size={16} />
              </div>
              <p className="text-sm font-semibold text-slate-800 m-0 mb-1">{feat.title}</p>
              <p className="text-xs text-slate-500 leading-relaxed m-0">{feat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default function CompliancePage() {
  const [activeTab, setActiveTab] = useState(TAB.HOME)
  const [featureState, setFeatureState] = useState({
    [TAB.SMP]:  { ...INITIAL_FEATURE_STATE },
    [TAB.RAID]: { ...INITIAL_FEATURE_STATE },
    [TAB.TVR]:  { ...INITIAL_FEATURE_STATE },
  })

  function patchFeature(feature, patch) {
    setFeatureState(prev => ({ ...prev, [feature]: { ...prev[feature], ...patch } }))
  }

  async function _startJob(feature, endpoint, formData) {
    try {
      const { data } = await api.post(`/compliance${endpoint}`, formData)
      patchFeature(feature, { jobId: data.job_id, phase: 'progress' })
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to start the pipeline.'
      patchFeature(feature, { error: detail, phase: 'result' })
    }
  }

  async function handleSmpSubmit(sowFile, templateFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    if (templateFile) fd.append('template', templateFile)
    await _startJob(TAB.SMP, '/generate', fd)
  }

  async function handleRaidSubmit(sowFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    await _startJob(TAB.RAID, '/raid/generate', fd)
  }

  async function handleTvrSubmit(sowFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    await _startJob(TAB.TVR, '/tvr/generate', fd)
  }

  function handleComplete(feature, data) { patchFeature(feature, { jobData: data, phase: 'result' }) }
  function handleFail(feature, msg)      { patchFeature(feature, { error: msg,  phase: 'result' }) }
  function handleReset(feature)          { patchFeature(feature, { ...INITIAL_FEATURE_STATE }) }

  const isTool = activeTab === TAB.SMP || activeTab === TAB.RAID || activeTab === TAB.TVR
  const { phase, jobId, jobData, error } = isTool ? featureState[activeTab] : {}

  return (
    <div className="compliance-wrap">
      <nav className="idp-nav">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`idp-nav-tab${activeTab === key ? ' active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </nav>

      <div className="idp-page">
        {activeTab === TAB.HOME    && <HomeTab onNavigate={setActiveTab} />}
        {activeTab === TAB.HISTORY && <JobRunHistoryPage />}

        {isTool && (
          <>
            <div>
              <p className="idp-page-title">{FEATURE_META[activeTab].title}</p>
              <p className="idp-page-sub">{FEATURE_META[activeTab].sub}</p>
            </div>

            {phase === 'upload' && activeTab === TAB.SMP  && <UploadCard onSubmit={handleSmpSubmit} />}
            {phase === 'upload' && activeTab === TAB.RAID && <RaidUploadCard onSubmit={handleRaidSubmit} />}
            {phase === 'upload' && activeTab === TAB.TVR  && <TvrUploadCard onSubmit={handleTvrSubmit} />}
            {phase === 'progress' && (
              <ProgressCard
                jobId={jobId}
                jobType={activeTab}
                onComplete={data => handleComplete(activeTab, data)}
                onFail={msg => handleFail(activeTab, msg)}
              />
            )}
            {phase === 'result' && (
              <ResultCard
                jobId={jobId}
                jobData={jobData}
                jobType={activeTab}
                error={error}
                onReset={() => handleReset(activeTab)}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
